import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Plus, X } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReadOnly } from '../../contexts/ReadOnlyContext';
import { useTenantAuth } from '../../contexts/TenantAuthContext';
import AppIconImage from '../../components/AppIconImage';
import { PageLoader } from '../../components/common';
import ImageUploadField from '../../components/expert/ImageUploadField';
import { ErrorBanner, PageHeader, btnPrimary, helpClass, inputClass, labelClass, useDebouncedValue } from '../../components/expert/ui';
import {
  AgentSettings,
  AgentSettingsUpdate,
  AppSettings,
  AppSettingsUpdate,
  domainExpertApi,
  expertAgentApi,
  expertKeys,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';
import { ICON_CORNER_RADIUS } from '../../lib/imageToIconPng';
import { httpUrlOrNull } from '../../lib/string.lib';
import { tenantApi, type WidgetHomepageConfig } from '../../lib/tenant-api';
import { EXPERT_ROUTES } from '../../lib/workspace';
import { canAccess } from '../../lib/permissions';

const DEFAULT_COLOR = '#1f2937';
const DEFAULT_WELCOME = 'Hi {first name}, what can I help you find?';
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const MAX_PROMPTS = 4;
const SHORT_NAME_MAX = 12;

interface AppForm {
  app_name: string;
  app_short_name: string;
  app_icon_url: string;
  primary_color: string;
  welcome_message: string;
  suggested_prompts: string[];
}

interface AgentForm {
  custom_instructions: string;
  no_answer_message: string;
}

const appFormFrom = (app: AppSettings): AppForm => ({
  app_name: app.app_name ?? '',
  app_short_name: app.app_short_name ?? '',
  app_icon_url: app.app_icon_url ?? '',
  primary_color: app.primary_color ?? '',
  welcome_message: app.welcome_message ?? '',
  suggested_prompts: app.suggested_prompts ?? [],
});

const agentFormFrom = (agent: AgentSettings): AgentForm => ({
  custom_instructions: agent.custom_instructions ?? '',
  no_answer_message: agent.no_answer_message ?? '',
});

const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

function buildAppUpdate(form: AppForm, saved: AppSettings, showShortName: boolean): AppSettingsUpdate {
  const update: AppSettingsUpdate = {};
  const name = orNull(form.app_name);
  if (name !== (saved.app_name ?? null)) update.app_name = name;
  const shortName = showShortName ? orNull(form.app_short_name) : null;
  if (shortName !== (saved.app_short_name ?? null)) update.app_short_name = shortName;
  const iconUrl = orNull(form.app_icon_url);
  if (iconUrl !== (saved.app_icon_url ?? null)) update.app_icon_url = iconUrl;
  const color = orNull(form.primary_color);
  if ((color?.toLowerCase() ?? null) !== (saved.primary_color?.toLowerCase() ?? null)) update.primary_color = color;
  const welcome = orNull(form.welcome_message);
  if (welcome !== (saved.welcome_message ?? null)) update.welcome_message = welcome;
  const prompts = form.suggested_prompts.map((p) => p.trim()).filter(Boolean);
  if (JSON.stringify(prompts) !== JSON.stringify(saved.suggested_prompts ?? [])) update.suggested_prompts = prompts;
  return update;
}

function buildAgentUpdate(form: AgentForm, saved: AgentSettings): AgentSettingsUpdate {
  const update: AgentSettingsUpdate = {};
  const instructions = orNull(form.custom_instructions);
  if (instructions !== (saved.custom_instructions ?? null)) update.custom_instructions = instructions;
  const noAnswer = orNull(form.no_answer_message);
  if (noAnswer !== (saved.no_answer_message ?? null)) update.no_answer_message = noAnswer;
  return update;
}

function Preview({ name, shortName, iconUrl, color, welcome, prompts }: {
  name: string;
  shortName: string;
  iconUrl: string;
  color: string;
  welcome: string;
  prompts: string[];
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // A typed link is loaded once typing pauses; a cleared one shows at once.
  const settledIcon = useDebouncedValue(iconUrl);
  const iconSrc = httpUrlOrNull(iconUrl.trim() ? settledIcon : '');
  const initial = (name.trim()[0] ?? 'A').toUpperCase();
  // Shown as the app shows its icons: an image or the letter tile, a full square with the same
  // rounded corners.
  const icon = (size: string) =>
    iconSrc && iconSrc !== failedUrl ? (
      <AppIconImage src={iconSrc} alt="" onError={() => setFailedUrl(iconSrc)} className={`${size} flex-shrink-0`} />
    ) : (
      <div
        className={`${size} flex flex-shrink-0 items-center justify-center font-semibold text-white`}
        style={{ backgroundColor: color, borderRadius: `${ICON_CORNER_RADIUS * 100}%` }}
      >
        {initial}
      </div>
    );

  return (
    <div className="border border-gray-200 bg-gray-50 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Preview</p>
      <div className="flex items-start gap-6">
        <div className="flex w-16 flex-shrink-0 flex-col items-center gap-1">
          {icon('h-12 w-12 text-lg')}
          <span className="w-full truncate text-center text-[11px] text-gray-700">{shortName}</span>
        </div>
        <div className="min-w-0 flex-1 overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5">
          <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ backgroundColor: color }}>
            {icon('h-6 w-6 text-xs')}
            <span className="truncate text-sm font-semibold">{name}</span>
          </div>
          <div className="space-y-2 p-3">
            <p className="text-sm text-gray-800">{welcome.replace('{first name}', 'Alex')}</p>
            {prompts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {prompts.map((prompt, index) => (
                  <span key={`${prompt}-${index}`} className="rounded-full border px-2 py-0.5 text-xs" style={{ borderColor: color, color }}>
                    {prompt}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { hasExpert } = useWorkspace();
  const { isReadOnly } = useReadOnly();
  const { tenant, admin } = useTenantAuth();
  const queryClient = useQueryClient();

  const appQuery = useQuery({ queryKey: expertKeys.app(), queryFn: domainExpertApi.getAppSettings, enabled: hasExpert });
  const agentQuery = useQuery({ queryKey: expertKeys.agentSettings(), queryFn: expertAgentApi.getSettings, enabled: hasExpert });
  // Only to name the active home screen; it's chosen on the Home screen page.
  const homepageId = agentQuery.data?.homepage_id ?? null;
  const homepagesQuery = useQuery<WidgetHomepageConfig[]>({
    queryKey: ['widgetHomepages'],
    queryFn: tenantApi.getHomepages,
    enabled: hasExpert && homepageId !== null,
  });

  const [appForm, setAppForm] = useState<AppForm | null>(null);
  const [agentForm, setAgentForm] = useState<AgentForm | null>(null);
  const [colorError, setColorError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appQuery.data && !appForm) setAppForm(appFormFrom(appQuery.data));
  }, [appQuery.data, appForm]);

  useEffect(() => {
    if (agentQuery.data && !agentForm) setAgentForm(agentFormFrom(agentQuery.data));
  }, [agentQuery.data, agentForm]);

  if (appQuery.isInitialLoading || agentQuery.isInitialLoading) return <PageLoader fullScreen={false} />;

  if (appQuery.isError || agentQuery.isError || !appQuery.data || !agentQuery.data || !appForm || !agentForm) {
    const error = appQuery.error ?? agentQuery.error;
    return (
      <div>
        <PageHeader title="Settings" />
        {error ? <ErrorBanner message={getExpertErrorMessage(error, "We couldn't load your settings. Please refresh the page.")} /> : null}
      </div>
    );
  }

  const savedApp = appQuery.data;
  const savedAgent = agentQuery.data;
  const displayName = appForm.app_name.trim() || tenant?.name || savedApp.effective?.name || 'Expert app';
  const showShortName = displayName.length > SHORT_NAME_MAX;
  const color = HEX_PATTERN.test(appForm.primary_color) ? appForm.primary_color : savedApp.effective?.primary_color || DEFAULT_COLOR;
  const shortNameForPreview = showShortName ? appForm.app_short_name.trim() || displayName.slice(0, SHORT_NAME_MAX) : displayName;
  const prompts = appForm.suggested_prompts.map((p) => p.trim()).filter(Boolean);

  const appUpdate = buildAppUpdate(appForm, savedApp, showShortName);
  const agentUpdate = buildAgentUpdate(agentForm, savedAgent);
  const hasChanges = Object.keys(appUpdate).length > 0 || Object.keys(agentUpdate).length > 0;

  const setApp = <K extends keyof AppForm>(key: K, value: AppForm[K]) => setAppForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  const setAgent = <K extends keyof AgentForm>(key: K, value: AgentForm[K]) => setAgentForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = async () => {
    if (appForm.primary_color.trim() && !HEX_PATTERN.test(appForm.primary_color.trim())) {
      setColorError('Use a hex colour like #1f2937.');
      return;
    }
    setColorError(null);
    if (!hasChanges) return;

    setSaving(true);
    const errors: string[] = [];
    const [appResult, agentResult] = await Promise.allSettled([
      Object.keys(appUpdate).length > 0 ? domainExpertApi.updateAppSettings(appUpdate) : Promise.resolve(null),
      Object.keys(agentUpdate).length > 0 ? expertAgentApi.updateSettings(agentUpdate) : Promise.resolve(null),
    ]);

    if (appResult.status === 'fulfilled' && appResult.value) {
      queryClient.setQueryData(expertKeys.app(), appResult.value);
      setAppForm(appFormFrom(appResult.value));
    } else if (appResult.status === 'rejected' && !isRequestCancelled(appResult.reason)) {
      errors.push(getExpertErrorMessage(appResult.reason, "We couldn't save the app settings."));
    }

    if (agentResult.status === 'fulfilled' && agentResult.value) {
      queryClient.setQueryData(expertKeys.agentSettings(), agentResult.value);
      setAgentForm(agentFormFrom(agentResult.value));
    } else if (agentResult.status === 'rejected' && !isRequestCancelled(agentResult.reason)) {
      errors.push(getExpertErrorMessage(agentResult.reason, "We couldn't save the answer settings."));
    }

    setSaving(false);
    if (errors.length > 0) toast.error(errors.join(' '));
    else if (appResult.status === 'fulfilled' && agentResult.status === 'fulfilled') toast.success('Settings saved');
  };

  const disabled = isReadOnly || saving;
  const homeScreenName = homepageId
    ? homepagesQuery.data?.find((hp) => hp.id === homepageId)?.homepage_name ?? (homepagesQuery.isInitialLoading ? '…' : 'a homepage')
    : 'None';

  return (
    <div>
      <PageHeader title="Settings" description="How your expert app looks and how your agent answers." />

      <div className="mt-6 space-y-6">
        <section className="bg-white p-4 shadow ring-1 ring-black ring-opacity-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900">App</h2>
          <p className="mt-1 text-sm text-gray-500">What your users see when they open the app.</p>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="space-y-5">
              <div>
                <label htmlFor="app-name" className={labelClass}>
                  App name
                </label>
                <input
                  id="app-name"
                  type="text"
                  value={appForm.app_name}
                  maxLength={100}
                  placeholder={tenant?.name ?? ''}
                  disabled={disabled}
                  onChange={(e) => setApp('app_name', e.target.value)}
                  className={inputClass}
                />
              </div>

              {showShortName && (
                <div>
                  <label htmlFor="app-short-name" className={labelClass}>
                    Home-screen name
                  </label>
                  <input
                    id="app-short-name"
                    type="text"
                    value={appForm.app_short_name}
                    maxLength={SHORT_NAME_MAX}
                    placeholder={displayName.slice(0, SHORT_NAME_MAX)}
                    disabled={disabled}
                    onChange={(e) => setApp('app_short_name', e.target.value)}
                    className={inputClass}
                  />
                  <p className={helpClass}>Shown under the app icon on phones. Up to {SHORT_NAME_MAX} characters.</p>
                </div>
              )}

              <div>
                <label htmlFor="app-icon" className={labelClass}>
                  App icon
                </label>
                <ImageUploadField
                  id="app-icon"
                  value={appForm.app_icon_url}
                  onChange={(url) => setApp('app_icon_url', url)}
                  disabled={disabled}
                  placeholderColor={color}
                  placeholderText={(displayName[0] ?? 'A').toUpperCase()}
                />
              </div>

              <div>
                <label htmlFor="app-color" className={labelClass}>
                  Brand colour
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Pick brand colour"
                    value={color}
                    disabled={disabled}
                    onChange={(e) => setApp('primary_color', e.target.value)}
                    className="h-9 w-10 flex-shrink-0 cursor-pointer border border-gray-200 p-0.5"
                  />
                  <input
                    id="app-color"
                    type="text"
                    value={appForm.primary_color}
                    maxLength={7}
                    placeholder={DEFAULT_COLOR}
                    disabled={disabled}
                    onChange={(e) => setApp('primary_color', e.target.value)}
                    className={`${inputClass} max-w-[10rem] font-mono`}
                  />
                </div>
                {colorError && <p className="mt-1 text-sm text-red-600">{colorError}</p>}
              </div>

              <div>
                <span className={labelClass}>Home screen</span>
                <p className="text-sm text-gray-700">
                  {homeScreenName}
                  {canAccess(admin?.role, EXPERT_ROUTES.homeScreen) && (
                    <>
                      {' ('}
                      <Link to={EXPERT_ROUTES.homeScreen} className="font-medium text-indigo-600 hover:text-indigo-800">
                        change
                      </Link>
                      {')'}
                    </>
                  )}
                </p>
                <p className={helpClass}>
                  {homepageId
                    ? 'Shown at the top of a new chat instead of the welcome message.'
                    : 'Users see the welcome message and suggested questions.'}
                </p>
              </div>

              <div>
                <label htmlFor="app-welcome" className={labelClass}>
                  Welcome message
                </label>
                <textarea
                  id="app-welcome"
                  rows={3}
                  value={appForm.welcome_message}
                  maxLength={500}
                  placeholder={DEFAULT_WELCOME}
                  disabled={disabled}
                  onChange={(e) => setApp('welcome_message', e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <span className={labelClass}>Suggested questions</span>
                <div className="space-y-2">
                  {appForm.suggested_prompts.map((prompt, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        aria-label={`Suggested question ${index + 1}`}
                        value={prompt}
                        maxLength={200}
                        disabled={disabled}
                        onChange={(e) =>
                          setApp(
                            'suggested_prompts',
                            appForm.suggested_prompts.map((p, i) => (i === index ? e.target.value : p)),
                          )
                        }
                        className={inputClass}
                      />
                      <button
                        type="button"
                        aria-label={`Remove suggested question ${index + 1}`}
                        disabled={disabled}
                        onClick={() =>
                          setApp(
                            'suggested_prompts',
                            appForm.suggested_prompts.filter((_, i) => i !== index),
                          )
                        }
                        className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {appForm.suggested_prompts.length < MAX_PROMPTS && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setApp('suggested_prompts', [...appForm.suggested_prompts, ''])}
                    className="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add a question
                  </button>
                )}
                <p className={helpClass}>Up to {MAX_PROMPTS}. Users can tap one to ask it.</p>
              </div>
            </div>

            <div>
              <Preview
                name={displayName}
                shortName={shortNameForPreview}
                iconUrl={appForm.app_icon_url}
                color={color}
                welcome={appForm.welcome_message.trim() || DEFAULT_WELCOME}
                prompts={prompts}
              />
            </div>
          </div>
        </section>

        <section className="bg-white p-4 shadow ring-1 ring-black ring-opacity-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900">Answers</h2>
          <p className="mt-1 text-sm text-gray-500">How your agent answers your users.</p>

          <div className="mt-6 space-y-5">
            <div>
              <label htmlFor="agent-instructions" className={labelClass}>
                Instructions
              </label>
              <textarea
                id="agent-instructions"
                rows={8}
                value={agentForm.custom_instructions}
                maxLength={8000}
                disabled={disabled}
                onChange={(e) => setAgent('custom_instructions', e.target.value)}
                className={inputClass}
              />
              <p className={helpClass}>
                Tell your agent how to answer: tone, terminology, what to emphasize, what to avoid. ({agentForm.custom_instructions.length} / 8000)
              </p>
            </div>

            <div>
              <label htmlFor="agent-no-answer" className={labelClass}>
                When the agent can't find an answer
              </label>
              <textarea
                id="agent-no-answer"
                rows={3}
                value={agentForm.no_answer_message}
                maxLength={2000}
                placeholder={savedAgent.default_no_answer_message}
                disabled={disabled}
                onChange={(e) => setAgent('no_answer_message', e.target.value)}
                className={inputClass}
              />
              <p className={helpClass}>Leave blank to use the message shown.</p>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={handleSave} disabled={disabled || !hasChanges} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <p className="text-xs text-gray-500">Changes appear the next time users open the app. Installed icons can take a day to refresh.</p>
        </div>
      </div>
    </div>
  );
}
